import { HTMLAttributes, ReactNode, Ref, useCallback, type MouseEvent } from 'react';

import { ElevationsType, FlexAligner, LoadingBubbles, Typography } from '@scaleaq/scaleui';
import { surface } from '@scaleaq/sui-tokens/colors';
import { spacing } from '@scaleaq/sui-tokens/responsive';

import { Z_INDEX } from '@/constants';
import { Icon } from '@/utils';
import { responsive } from '@/utils';

import {
  StyledErrorContainer,
  StyledLoading,
  StyledOuterWrapper,
} from './styledCardWrapperWithState.style.ts';

const CARD_WRAPPER_BORDER_RADIUS = `${responsive.radius.large}`;
type StyledCardWrapperWithStateProps = HTMLAttributes<HTMLDivElement> & {
  children?: ReactNode;
  isError?: boolean;
  errorMessage?: string;
  isLoading?: boolean;
  isEmpty?: boolean;
  emptyMessages?: string[];

  borderRadius?: string;
  hideBorder?: boolean;
  loadingZIndex?: number;
  surfaceColor?: string;
  elevation?: keyof ElevationsType;
  mainTitle?: string;
  padding?: string;
  titlePadding?: string;
  gap?: string;

  ref?: Ref<HTMLDivElement>;
};
export const StyledCardWrapperWithState = ({
  mainTitle,
  children,
  borderRadius = CARD_WRAPPER_BORDER_RADIUS,
  hideBorder = false,
  isLoading,
  isError,
  isEmpty,
  emptyMessages = ['No data to show'],
  errorMessage,
  loadingZIndex = Z_INDEX.overlay.loading,
  titlePadding = '0',
  padding = '0',
  surfaceColor = surface.cards,
  elevation = 2,
  gap = spacing['4xlarge'],
  onContextMenu,
  ...rest
}: StyledCardWrapperWithStateProps) => {
  const handleContextMenuCapture = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!onContextMenu) return;
      e.preventDefault();
      onContextMenu(e);
    },
    [onContextMenu]
  );

  return (
    <StyledOuterWrapper
      color={surfaceColor}
      elevation={elevation}
      $padding={padding}
      $borderRadius={borderRadius}
      $error={isError}
      $hideBorder={hideBorder}
      onContextMenu={onContextMenu ? handleContextMenuCapture : undefined}
      {...rest}
    >
      {isLoading && (
        <StyledLoading
          alignment='center'
          fullHeight
          $borderRadius={borderRadius}
          $loadingZIndex={loadingZIndex}
        >
          <LoadingBubbles size='large' />
        </StyledLoading>
      )}
      {isEmpty && (
        <StyledLoading
          direction='column'
          alignment='center'
          $borderRadius={borderRadius}
          $loadingZIndex={loadingZIndex}
        >
          {emptyMessages?.map((message: string, index: number) => (
            <Typography key={message + index}>{message}</Typography>
          ))}
        </StyledLoading>
      )}
      {isError && errorMessage && (
        <StyledErrorContainer
          direction='column'
          alignment='center'
          fullHeight
          $errorZIndex={loadingZIndex + 1}
          $borderRadius={borderRadius}
        >
          <Icon.TriangleAlert size='large' />
          <Typography type='body_large'>{errorMessage}</Typography>
        </StyledErrorContainer>
      )}
      <FlexAligner direction='column' gap={gap} fullHeight>
        {mainTitle && (
          <Typography
            key='1'
            type='heading_medium'
            style={{ zIndex: loadingZIndex + 2, padding: titlePadding }}
          >
            {`${mainTitle}`}
          </Typography>
        )}
        {children}
      </FlexAligner>
    </StyledOuterWrapper>
  );
};
